import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

import {
  SESSION_EXPIRED_INACTIVITY_REASON,
  SESSION_EXPIRED_REASON_PARAM,
} from "@/constants/session";
import { clearAuthenticatedSessionQueries } from "@/lib/authQueryCache";
import { type SessionExpiredReason, subscribeSessionExpired } from "@/lib/sessionExpiry";
import { useUserStore } from "@/store/userStore";

export function clearClientSession(queryClient: QueryClient) {
  clearAuthenticatedSessionQueries(queryClient);
  useUserStore.getState().clearSession();
  useUserStore.persist.clearStorage();
}

export function buildLoginRedirectUrl(
  reason: SessionExpiredReason | null,
  intended: string
): string {
  const params = new URLSearchParams();
  if (reason === "inactivity") {
    params.set(SESSION_EXPIRED_REASON_PARAM, SESSION_EXPIRED_INACTIVITY_REASON);
  }
  if (intended && intended !== "/") {
    params.set("redirect", intended);
  }

  const query = params.toString();
  return query ? `/login?${query}` : "/login";
}

export function useSessionExpiredRedirect() {
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(
    () =>
      subscribeSessionExpired((reason) => {
        clearClientSession(queryClient);
        const intended = window.location.pathname + window.location.search;
        router.replace(buildLoginRedirectUrl(reason, intended));
        router.refresh();
      }),
    [queryClient, router]
  );
}
